import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { addDays, subDays } from 'date-fns';
import { EMPTY, catchError, tap } from 'rxjs';
import { CollectedServiceService } from 'src/app/generated/backend/services';
import { mapAndGroupServiceOverviewDto } from 'src/app/utils/collected-services.utils';
import {
  formatDateString,
  getFromDate,
  getToDate,
} from 'src/app/utils/datetime.utils';
import {
  LoadCollectedServicesAction,
  ResetCollectedServicesAction,
  SetNextWeekAction,
  SetPreviousWeekAction,
  SetSelectedDayAction,
  SetServiceFilterAction,
  SetTodayAction,
  ToggleHourlyViewAction,
} from './collected-services.actions';
import { CollectedServicesStateModel, ServiceFilter } from './collected-services.state-model';

export const collectedServicesStateDefaults: CollectedServicesStateModel = {
  from: getFromDate(),
  to: getToDate(),
  selected: new Date(),
  groupedServiceOverview: [],
  loading: false,
  error: false,
  filter: ServiceFilter.ALL,
  isHourlyView: false,
};

@State<CollectedServicesStateModel>({
  name: 'collectedServices',
  defaults: collectedServicesStateDefaults,
})
@Injectable()
export class CollectedServicesState {
  constructor(private collectedService: CollectedServiceService) {}

  @Action(LoadCollectedServicesAction)
  load(ctx: StateContext<CollectedServicesStateModel>) {
    ctx.patchState({ loading: true });
    return this.collectedService
      .apiCollectedServiceStartDateStringEndDateStringGet$Json({
        startDateString: formatDateString(ctx.getState().from),
        endDateString: formatDateString(ctx.getState().to),
      })
      .pipe(
        tap((response) => {
          ctx.patchState({
            loading: false,
            error: false,
            groupedServiceOverview: mapAndGroupServiceOverviewDto(response),
          });
        }),
        catchError((error) => {
          console.error(error);
          ctx.patchState({
            loading: false,
            error: true,
            groupedServiceOverview: [],
          });
          return EMPTY;
        })
      );
  }

  @Action(SetSelectedDayAction)
  setSelected(
    ctx: StateContext<CollectedServicesStateModel>,
    { day }: SetSelectedDayAction
  ) {
    ctx.patchState({ selected: day });
  }

  @Action(SetPreviousWeekAction)
  setPreviousWeek(ctx: StateContext<CollectedServicesStateModel>) {
    ctx.patchState({
      from: subDays(ctx.getState().from, 7),
      to: subDays(ctx.getState().to, 7),
      selected: subDays(ctx.getState().from, 7),
    });
    return ctx.dispatch(new LoadCollectedServicesAction());
  }

  @Action(SetNextWeekAction)
  setNextWeek(ctx: StateContext<CollectedServicesStateModel>) {
    ctx.patchState({
      from: addDays(ctx.getState().from, 7),
      to: addDays(ctx.getState().to, 7),
      selected: addDays(ctx.getState().from, 7),
    });
    return ctx.dispatch(new LoadCollectedServicesAction());
  }

  @Action(SetTodayAction)
  setToday(ctx: StateContext<CollectedServicesStateModel>) {
    ctx.patchState({
      from: getFromDate(),
      to: getToDate(),
      selected: new Date(),
    });
    return ctx.dispatch(new LoadCollectedServicesAction());
  }

  @Action(ResetCollectedServicesAction)
  reset(ctx: StateContext<CollectedServicesStateModel>) {
    ctx.setState(collectedServicesStateDefaults);
  }

  @Action(SetServiceFilterAction)
  setServiceFilter(
    ctx: StateContext<CollectedServicesStateModel>,
    { filter }: SetServiceFilterAction
  ) {
    ctx.patchState({ filter });
  }

  @Action(ToggleHourlyViewAction)
  toggleHourlyView(ctx: StateContext<CollectedServicesStateModel>) {
    const currentState = ctx.getState();
    ctx.patchState({ isHourlyView: !currentState.isHourlyView });
  }
}
